import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { CheckCircle2, AlertCircle, X } from 'lucide-react'

// Snackbars for the outcome of an action (sent, saved, failed). Errors that
// explain why a page cannot load stay inline (ErrorState); these are for
// one-off results the admin should notice without them blocking the page.

type Tone = 'success' | 'error'
interface Toast {
	id: number
	tone: Tone
	message: string
}

const ToastContext = createContext<((message: string, tone?: Tone) => void) | null>(null)

// Errors stay up longer: they usually need reading.
const DURATION: Record<Tone, number> = { success: 4000, error: 8000 }

export function ToastProvider({ children }: { children: React.ReactNode }) {
	const [toasts, setToasts] = useState<Toast[]>([])
	const nextId = useRef(1)

	const dismiss = useCallback((id: number) => setToasts((list) => list.filter((t) => t.id !== id)), [])
	const show = useCallback((message: string, tone: Tone = 'success') => {
		const id = nextId.current++
		// At most three on screen; the oldest makes room.
		setToasts((list) => [...list.slice(-2), { id, tone, message }])
	}, [])

	return (
		<ToastContext.Provider value={show}>
			{children}
			<div
				aria-live="polite"
				className="pointer-events-none fixed inset-x-4 bottom-4 z-[100] flex flex-col items-center gap-2 sm:inset-x-auto sm:right-5 sm:items-end"
			>
				{toasts.map((t) => (
					<ToastItem key={t.id} toast={t} dismiss={dismiss} />
				))}
			</div>
		</ToastContext.Provider>
	)
}

// `dismiss` is stable, so each toast's timer runs once from when it appeared,
// however many others come and go meanwhile.
function ToastItem({ toast, dismiss }: { toast: Toast; dismiss: (id: number) => void }) {
	useEffect(() => {
		const timer = setTimeout(() => dismiss(toast.id), DURATION[toast.tone])
		return () => clearTimeout(timer)
	}, [toast.id, toast.tone, dismiss])

	const Icon = toast.tone === 'success' ? CheckCircle2 : AlertCircle
	return (
		<div
			role={toast.tone === 'error' ? 'alert' : 'status'}
			className="pointer-events-auto flex w-full max-w-sm items-start gap-2.5 rounded-lg bg-ink px-4 py-3 text-sm text-white shadow-lg"
		>
			<Icon className={`mt-0.5 h-4 w-4 shrink-0 ${toast.tone === 'success' ? 'text-green-400' : 'text-red-400'}`} />
			<p className="min-w-0 flex-1 break-words">{toast.message}</p>
			<button type="button" onClick={() => dismiss(toast.id)} aria-label="Dismiss" className="-mr-1 shrink-0 rounded p-0.5 text-white/70 hover:text-white">
				<X className="h-4 w-4" />
			</button>
		</div>
	)
}

export function useToast() {
	const show = useContext(ToastContext)
	if (!show) throw new Error('useToast must be used inside <ToastProvider>')
	return show
}
